import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import OnboardingModal from './OnboardingModal';
import LoginModal from './LoginModal';
import { registerUser } from '../firebase/auth';
import { FirebaseService } from '../services/firebaseService';
import { api } from '../utils/api';
import {
  isDisposableEmail,
  normalizeEmail,
  normalizePhone,
  validateCompanyName,
  validateEmail,
  validatePasswordStrength,
  validatePersonName,
  validatePhone,
} from '../utils/validation';
import BackToMarketing from './BackToMarketing';

export default function Registration({ onRegister, selectedPlan }) {
  // Map URL plan parameter to form plan
  const getPlanFromUrl = (planParam) => {
    if (!planParam) return 'free';
    const planMap = {
      free: 'free',
      basic: 'free',
      pro: 'pro',
      premium: 'enterprise',
      enterprise: 'enterprise'
    };
    const key = String(planParam).toLowerCase();
    return planMap[key] || 'free';
  };

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    password: '',
    confirmPassword: '',
    plan: getPlanFromUrl(selectedPlan)
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Form, 2: Payment, 3: Success
  const [showLoginModal, setShowLoginModal] = useState(false); // Login modal state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false); // Onboarding modal state
  const [clientData, setClientData] = useState(null); // Client data for onboarding
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const plans = [
    {
      id: 'free',
      name: 'Demo & build request',
      price: 'Free',
      period: '',
      features: [
        'Create a demo voice agent',
        'Test in your dashboard',
        'Request production setup (consultant contact)',
        'No payment in Phase 1'
      ],
      paypalAmount: '0.00',
      paypalItem: 'AppifyAI Demo'
    }
  ];

  const currentPlan = plans.find(plan => plan.id === form.plan);

  const validateForm = () => {
    const newErrors = {};

    const firstNameError = validatePersonName(form.firstName);
    if (firstNameError) newErrors.firstName = firstNameError;

    const lastNameError = validatePersonName(form.lastName);
    if (lastNameError) newErrors.lastName = lastNameError;

    const normalizedEmail = normalizeEmail(form.email);
    if (!normalizedEmail) newErrors.email = 'Email is required';
    else if (!validateEmail(normalizedEmail)) newErrors.email = 'Email is invalid';
    else if (isDisposableEmail(normalizedEmail)) newErrors.email = 'Please use your real business or personal email';

    const companyError = validateCompanyName(form.company);
    if (companyError) newErrors.company = companyError;

    const phoneError = validatePhone(form.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (!form.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordCheck = validatePasswordStrength(form.password);
      if (!passwordCheck.valid) {
        newErrors.password = `Password must include ${passwordCheck.issues.join(', ')}`;
      }
    }

    if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    const normalized = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: normalizeEmail(form.email),
      company: form.company.trim(),
      phone: normalizePhone(form.phone),
      password: form.password,
      plan: form.plan,
    };

    try {
      // Check if email already exists
      const emailExists = await FirebaseService.checkEmailExists(normalized.email);
      if (emailExists) {
        setErrors({ general: 'An account with this email already exists. Please use a different email or try logging in.' });
        return;
      }
      
      // Register user with Firebase Authentication
      const result = await registerUser(normalized.email, normalized.password, {
        firstName: normalized.firstName,
        lastName: normalized.lastName,
        company: normalized.company,
        phone: normalized.phone,
        plan: normalized.plan
      });
      
      if (result.success) {
        // Check if email verification is required
        if (result.requiresVerification) {
          // Create client data for Firebase
          const clientData = {
            id: result.user.uid,
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName,
            firstName: normalized.firstName,
            lastName: normalized.lastName,
            company: normalized.company,
            phone: normalized.phone,
            plan: normalized.plan,
            status: 'pending_verification'
          };
          
          // Save client to Firebase
          await FirebaseService.createClient(clientData);
          // Notify admin (fire-and-forget; do not block redirect)
          api('/api/onboarding/notify-signup', {
            method: 'POST',
            body: JSON.stringify({
              email: normalized.email,
              firstName: normalized.firstName,
              lastName: normalized.lastName,
              company: normalized.company,
              phone: normalized.phone,
              plan: normalized.plan,
            }),
          }).catch(() => {});
          
          // Store user data in localStorage
          localStorage.setItem('user', JSON.stringify(clientData));
          
          // Redirect to email verification
          window.location.href = '/verify-email';
          return;
        }
        
        // Create client data for Firebase
        const clientData = {
          id: result.user.uid,
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          firstName: normalized.firstName,
          lastName: normalized.lastName,
          company: normalized.company,
          phone: normalized.phone,
          plan: normalized.plan,
          status: 'active'
        };
        
        // Save client to Firebase
        await FirebaseService.createClient(clientData);
        // Notify admin (fire-and-forget)
        api('/api/onboarding/notify-signup', {
          method: 'POST',
          body: JSON.stringify({
            email: normalized.email,
            firstName: normalized.firstName,
            lastName: normalized.lastName,
            company: normalized.company,
            phone: normalized.phone,
            plan: normalized.plan,
          }),
        }).catch(() => {});
        
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(clientData));
        
        handleFreePlanSuccess(clientData);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Registration failed. Please try again.';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please use a different email or try logging in.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email/password accounts are not enabled. Please contact support.';
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleFreePlanSuccess = async (clientData) => {
    setPaymentLoading(true);
    
    try {
      // Simulate a brief delay for UX
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update client data with payment info
      const updatedClientData = {
        ...clientData,
        paymentId: 'free_plan_' + Date.now(),
        paymentStatus: 'completed',
        features: currentPlan.features,
        assistantId: 'pending_assistant_creation',
        whatsappNumber: '+1 (555) 123-4567',
        voiceNumber: '+1 (555) 987-6543'
      };
      
      // Update client in Firebase
      await FirebaseService.updateClient(updatedClientData.id, {
        paymentId: updatedClientData.paymentId,
        paymentStatus: 'completed',
        features: currentPlan.features
      });
      
      // Store client data in state
      setClientData(updatedClientData);
      
      // Update localStorage with complete client data
      localStorage.setItem('user', JSON.stringify(updatedClientData));
      
      setPaymentSuccess(true);
      setCurrentStep(3);
      window.location.href = '/onboarding';
    } catch (error) {
      console.error('Free plan setup error:', error);
      setErrors({ general: 'Account setup failed. Please try again.' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSuccess = async (details) => {
    setPaymentLoading(true);
    
    try {
      // Get existing client data from localStorage
      const existingClientData = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!existingClientData.id) {
        throw new Error('Client data not found. Please try again.');
      }
      
      // Update client with payment information
      const updatedClientData = {
        ...existingClientData,
        paymentId: details.id,
        paymentStatus: 'completed',
        features: currentPlan.features,
        assistantId: 'pending_assistant_creation',
        whatsappNumber: '+1 (555) 123-4567',
        voiceNumber: '+1 (555) 987-6543'
      };
      
      // Update client in Firebase
      await FirebaseService.updateClient(existingClientData.id, {
        paymentId: details.id,
        paymentStatus: 'completed',
        features: currentPlan.features
      });
      
      console.log('✅ Payment processed and client updated in Firebase');
      
      // Store updated client data
      localStorage.setItem('user', JSON.stringify(updatedClientData));
      
      // Store client data in state
      setClientData(updatedClientData);
      
      setPaymentSuccess(true);
      setCurrentStep(3);
      
      // Show onboarding modal for agent setup
      setShowOnboardingModal(true);
      
      if (onRegister) {
        onRegister(updatedClientData);
      }
      
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrors({ general: 'Payment processed but account update failed. Please contact support.' });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setErrors({ general: 'Payment failed. Please try again.' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const renderFormStep = () => (
    <div className="w-full px-4">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)', boxShadow: '0 0 40px rgba(139,92,246,0.3)' }}>
          <img
            src="/screenshots/appymascot.png.png"
            alt="Appy - The Appify AI Mascot"
            className="w-10 h-10 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-10 h-10 rounded-full bg-white/10 text-white items-center justify-center font-bold text-sm">
            Appy
          </div>
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-1">
          Appify.AI
        </h1>
        <h2 className="text-lg font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Create your account</h2>
        <div className="flex flex-wrap items-center justify-center mt-2 gap-2 sm:gap-3">
          {['No Setup Fees', 'Cancel Anytime', '24/7 Support'].map(label => (
            <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(139,92,246,0.1)', border: '0.5px solid rgba(139,92,246,0.2)', color: 'rgba(255,255,255,0.6)' }}>
              <svg className="w-3 h-3 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {label}
            </div>
          ))}
        </div>
          </div>

      {/* Main Content - Centered Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 max-w-6xl mx-auto items-center justify-items-center">
        {/* Registration Form */}
        <div className="w-full max-w-lg">
          <div className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center mb-5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Account Information</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Tell us about yourself</p>
              </div>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-3">
              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                    placeholder="John"
                  />
                  {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                    placeholder="Doe"
                  />
                  {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                  placeholder="john@company.com"
                />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>Use a real email address. Temporary/disposable inboxes are blocked.</p>
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Company Name</label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                  placeholder="Acme Corp"
                />
                {errors.company && <p className="text-red-400 text-xs mt-1">{errors.company}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(prev => !prev)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white focus:outline-none"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                  <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>10+ chars with upper, lower, number, and symbol.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white focus:outline-none"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>


              {/* General Error */}
              {errors.general && (
                <div className="text-red-400 text-xs text-center">{errors.general}</div>
              )}

              {/* Continue Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl text-sm text-white font-semibold transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {form.plan === 'free' ? (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Create Free Account
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        Continue to Payment
                      </>
                    )}
                  </div>
                )}
              </button>

              {/* Login Link */}
              <div className="text-center mt-4">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Already have an account?{' '}
                  <a href="/login" className="text-purple-400 hover:text-purple-300 font-medium transition">
                    Sign in here
                  </a>
                </p>
                
              </div>
            </form>
                </div>
              </div>

        {/* How It Works */}
        <div className="w-full max-w-lg">
          <div className="p-6 md:p-8 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
            <h3 className="text-lg font-bold text-white mb-6">How It Works</h3>
            <ol className="space-y-5">
              {[
                'Sign up for a free account',
                'Create your AI voice agent in minutes',
                'Test it instantly — speak directly to your agent from the dashboard',
                'Request a full production build — our team handles setup, integrations, and automation',
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                    {i + 1}
                  </span>
                  <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>{text}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPaymentStep = () => (
    <div className="w-full max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Complete Your Payment</h2>
        <p className="text-gray-400">Secure payment powered by PayPal</p>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
        {/* Order Summary */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Order Summary</h3>
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-300">{currentPlan.name}</span>
              <span className="text-white font-semibold">{currentPlan.price}{currentPlan.period}</span>
            </div>
            <div className="text-sm text-gray-400">
              Billed monthly • Cancel anytime
            </div>
          </div>
        </div>

        {/* PayPal Integration */}
        <div className="mb-6">
          {(paymentLoading || paymentSuccess) && (
            <p className="text-center text-sm text-cyan-300 mb-4" aria-live="polite">
              {paymentLoading ? 'Processing payment…' : 'Payment received.'}
            </p>
          )}
          <PayPalScriptProvider options={{ 
            "client-id": "AUneAqTLHJUsYxOtvOwMGELnsi1520JyLSDtfThe1odiT_oyXLkzE_6S19qn0cAZI8_csM0PhDAOVTq7",
            currency: "USD"
          }}>
            <PayPalButtons
              createOrder={(data, actions) => {
                return actions.order.create({
                  purchase_units: [{
                    amount: {
                      value: currentPlan.paypalAmount,
                      currency_code: "USD"
                    },
                    description: currentPlan.paypalItem
                  }]
                });
              }}
              onApprove={(data, actions) => {
                return actions.order.capture().then((details) => {
                  handlePaymentSuccess(details);
                });
              }}
              onError={handlePaymentError}
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'paypal'
              }}
            />
          </PayPalScriptProvider>
        </div>

        {/* Back Button */}
        <button
          onClick={() => setCurrentStep(1)}
          className="w-full py-3 px-4 bg-white/10 border border-white/20 rounded-lg text-white font-semibold hover:bg-white/20 transition"
        >
          Back to Form
        </button>

              {/* General Error */}
              {errors.general && (
          <div className="text-red-400 text-sm text-center mt-4">{errors.general}</div>
        )}
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="w-full max-w-4xl mx-auto text-center">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
                  </div>
        
        <h2 className="text-3xl font-bold text-white mb-4">Welcome to Appify.AI!</h2>
        <p className="text-gray-300 mb-6">
          Your account has been created successfully. We're setting up your AI assistant and phone number.
        </p>
        
        <div className="bg-white/5 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-white mb-2">What's Next?</h3>
          <ul className="text-left text-gray-300 space-y-2">
            <li>• Your AI assistant is being configured</li>
            <li>• Phone number assignment in progress</li>
            <li>• Voice agent configuration in progress</li>
            <li>• Welcome email sent to {form.email}</li>
          </ul>
              </div>
        
        <button
          onClick={() => setShowOnboardingModal(true)}
          className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg text-white font-semibold hover:from-purple-700 hover:to-blue-700 transition"
        >
          Set Up Your AI Assistant
        </button>
          </div>
        </div>
  );

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      <div className="fixed top-4 left-4 md:top-6 md:left-6 z-[99999] pointer-events-auto">
        <BackToMarketing className="px-3 py-2 rounded-lg bg-black/30 backdrop-blur-sm border border-white/10 hover:bg-white/10" />
      </div>

      <div className="relative z-20 w-full min-h-screen py-6 px-4 flex items-center justify-center">
        {currentStep === 1 && renderFormStep()}
        {currentStep === 2 && renderPaymentStep()}
        {currentStep === 3 && renderSuccessStep()}
      </div>
      
      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        onSuccess={(user, userData) => {
          if (onRegister) {
            onRegister({ user, userData });
          }
          setShowLoginModal(false);
        }}
      />
      
      {/* Onboarding Modal */}
      <OnboardingModal 
        isOpen={showOnboardingModal} 
        onClose={() => setShowOnboardingModal(false)} 
        clientData={clientData}
        onComplete={(agentData) => {
          // Navigate to dashboard after agent setup
          navigate('/dashboard');
        }}
      />
    </div>
  );
}